import { ReactElement } from 'react';
import {
  BootstrapDropdown,
  BootstrapDropdownProps,
  BootstrapSubmenuProps
} from './BootstrapDropdown';
import { DropdownVariant } from './dropdown-constants';
import {
  ReactSelectCellSingleProps,
  ReactSelectDropdown,
  ReactSelectMultiProps,
  ReactSelectSingleProps,
  ReactSelectSubmenuProps
} from './ReactSelectDropdown';

type SubmenuDropdownProps<T extends string> = BootstrapSubmenuProps<T> | ReactSelectSubmenuProps<T>;

type DropdownProps<T extends string> =
  | BootstrapDropdownProps<T>
  | BootstrapSubmenuProps<T>
  | ReactSelectSingleProps<T>
  | ReactSelectMultiProps<T>
  | ReactSelectSubmenuProps<T>
  | ReactSelectCellSingleProps<T>;

const isSubmenuProps = <T extends string>(
  props: DropdownProps<T>
): props is SubmenuDropdownProps<T> => 'submenuValue' in props && props.submenuValue != null;

const IGNORE_FLAT_CHANGE = () => {};

const isSearchableSubmenuProps = <T extends string>(
  props: SubmenuDropdownProps<T>
): props is ReactSelectSubmenuProps<T> => props.searchable === true;

// Multi-select with an array value always goes through ReactSelectDropdown — BootstrapDropdown
// only handles single-value or externally-managed-via-menuItems multi-selects.
// Reads `multiSelect` / `value` off the union via narrowing; both fields are
// declared (with compatible types) on every variant of `DropdownProps<T>`.
const shouldUseReactSelectVariant = <T extends string>(
  props: Exclude<DropdownProps<T>, SubmenuDropdownProps<T>>
): boolean => {
  if (props.searchable) return true;
  if (props.variant === DropdownVariant.CELL) return true;
  return props.multiSelect === true && Array.isArray(props.value);
};

export const Dropdown = <T extends string>(props: DropdownProps<T>): ReactElement => {
  if (isSubmenuProps(props)) {
    if (isSearchableSubmenuProps(props)) {
      const { clearable, onClear } = props;
      return (
        <ReactSelectDropdown
          {...props}
          clearable={clearable ?? Boolean(onClear)}
          onChange={IGNORE_FLAT_CHANGE}
        />
      );
    }
    return <BootstrapDropdown {...props} onChange={IGNORE_FLAT_CHANGE} />;
  }

  const { searchable, ...rest } = props;

  if (shouldUseReactSelectVariant<T>(props)) {
    const rsProps = rest as
      ReactSelectSingleProps<T> | ReactSelectCellSingleProps<T> | ReactSelectMultiProps<T>;
    const empty: T[] = [];
    const rsOnChange = (newValue: T | T[] | null): void => {
      if (rsProps.multiSelect) {
        rsProps.onChange(Array.isArray(newValue) ? newValue : empty);
      } else {
        rsProps.onChange(Array.isArray(newValue) ? (newValue[0] ?? null) : newValue);
      }
    };
    return (
      <ReactSelectDropdown
        {...rsProps}
        searchable={searchable}
        clearable={rsProps.clearable ?? Boolean(rsProps.onClear)}
        showChips={rsProps.multiSelect ? rsProps.showChips : undefined}
        allSelectedLabel={rsProps.multiSelect ? rsProps.allSelectedLabel : undefined}
        onChange={rsOnChange}
      />
    );
  }

  return <BootstrapDropdown {...(rest as BootstrapDropdownProps<T>)} />;
};
